import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { furnitureService, FurnitureData } from '../services/furniture';

const Home: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [featuredItems, setFeaturedItems] = useState<FurnitureData[]>([]);

  useEffect(() => {
    let isMounted = true;

    furnitureService.getFurniture({
      page: 1,
      limit: 8,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }).then(response => {
      if (isMounted) setFeaturedItems(response.furniture || []);
    }).catch(() => {
      if (isMounted) setFeaturedItems([]);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Update active category based on URL
  useEffect(() => {
    // If we're not on the home page
    if (location.pathname !== '/') {
      const searchParams = new URLSearchParams(location.search);
      const categoryParam = searchParams.get('category');
      
      if (categoryParam) {
        setActiveCategory(categoryParam);
      } else {
        // If on furniture page without category, don't highlight any specific category
        setActiveCategory('');
      }
    } else {
      // On home page, 'all' is active
      setActiveCategory('all');
    }
  }, [location]);

  // Animation effect on scroll
  useEffect(() => {
    let observer: IntersectionObserver | undefined;
    const timeout = window.setTimeout(() => {
      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.classList.remove('opacity-0');
          }
        });
      }, { threshold: 0.1 });
      
      document.querySelectorAll('.fade-in-section').forEach(section => {
        observer?.observe(section);
      });
    }, 100);
    
    return () => {
      window.clearTimeout(timeout);
      observer?.disconnect();
    };
  }, []);

  // Category navigation
  const categories = [
    { id: 'all', name: t('home.categories.all') },
    { id: 'sofa', name: t('home.categories.sofa') },
    { id: 'chair', name: t('home.categories.chair') },
    { id: 'table', name: t('home.categories.table') },
    { id: 'bed', name: t('home.categories.bed') },
    { id: 'storage', name: t('home.categories.storage') },
    { id: 'desk', name: t('home.categories.desk') },
    { id: 'lighting', name: t('home.categories.lighting') }
  ];

  // Filter items based on active category
  const filteredItems = activeCategory === 'all'
    ? featuredItems
    : featuredItems.filter(item => item.category === activeCategory);

  // Collections for homepage
  const collections = [
    {
      title: t('home.collectionEssentials'),
      description: t('home.collectionEssentialsDesc'),
      image: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?ixlib=rb-4.0.3&auto=format&fit=crop&w=1770&q=80',
      link: '/furniture?category=desk'
    },
    {
      title: t('home.collectionSmall'),
      description: t('home.collectionSmallDesc'),
      image: 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1770&q=80',
      link: '/furniture?category=storage'
    },
    {
      title: t('home.collectionMoving'),
      description: t('home.collectionMovingDesc'),
      image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1770&q=80',
      link: '/furniture'
    }
  ];

  return (
    <div className="bg-white font-serif">
      {/* Main hero section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            className="w-full h-full object-cover"
            src="https://images.unsplash.com/photo-1631679706909-1844bbd07221?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Stylish living room"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto py-28 px-4 sm:py-32 sm:px-6 lg:px-8 flex flex-col items-center text-center">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            <span className="block">{t('home.hero.title1')}</span>
            <span className="block mt-2 text-yellow-400">{t('home.hero.title2')}</span>
          </h1>
          <p className="mt-6 text-xl text-gray-100 max-w-2xl">
            {t('home.hero.subtitle')}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/furniture" className="inline-block bg-white py-4 px-8 rounded-md font-medium text-gray-900 hover:bg-gray-100 transition-all">
              {t('home.hero.browseCta')}
            </Link>
            <Link to="/sell" className="inline-block bg-transparent py-4 px-8 rounded-md font-medium text-white border border-white hover:bg-white/10 transition-all">
              {t('home.hero.sellCta')}
            </Link>
          </div>
        </div>
      </div>

      {/* Category navigation */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto py-4" aria-label="Categories">
            {categories.map((category) => (
              category.id === 'all' ? (
                <Link
                  key={category.id}
                  to="/"
                  className={`whitespace-nowrap text-sm font-medium px-1 py-2 border-b-2 ${
                    activeCategory === category.id
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.name}
                </Link>
              ) : (
                <Link
                  key={category.id}
                  to={`/furniture?category=${category.id}`}
                  className={`whitespace-nowrap text-sm font-medium px-1 py-2 border-b-2 ${
                    activeCategory === category.id
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.name}
                </Link>
              )
            ))}
          </nav>
        </div>
      </div>

      {/* Featured Collections */}
      <div className="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('home.featuredCollections')}</h2>
        <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
          {collections.map((collection, index) => (
            <Link to={collection.link} key={index} className="group">
              <div className="relative h-80 overflow-hidden rounded-lg">
                <img
                  src={collection.image}
                  alt={collection.title}
                  className="h-full w-full object-cover object-center group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white">{collection.title}</h3>
                  <p className="mt-1 text-sm text-gray-200">{collection.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {filteredItems.length > 0 && (
      <div className="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
        <div className="flex justify-between items-end mb-8">
          <h2 className="text-3xl font-bold text-gray-900">{t('home.recentlyAdded')}</h2>
          <Link to="/furniture" className="text-sm font-medium text-gray-900 hover:text-gray-700 flex items-center">
            {t('home.viewAll')}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
        
        <div className="grid grid-cols-2 gap-y-10 gap-x-6 sm:grid-cols-3 lg:grid-cols-4">
          {filteredItems.map((item) => (
            <Link to={`/furniture/${item._id}`} key={item._id} className="group">
              <div className="relative">
                <div className="aspect-w-1 aspect-h-1 overflow-hidden">
                  <img
                    src={item.images[0]}
                    alt={item.title}
                    className="h-full w-full object-cover object-center group-hover:opacity-90 transition-all duration-300"
                  />
                </div>
                <div className="mt-4">
                  <h3 className="text-sm text-gray-700">{item.title}</h3>
                  <p className="mt-1 text-lg font-medium text-gray-900">${item.price}</p>
                  <div className="flex items-center mt-1">
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${
                      ['excellent', 'new', 'like-new'].includes(item.condition.toLowerCase())
                        ? 'bg-green-500' 
                        : item.condition === 'good'
                          ? 'bg-yellow-500' 
                          : 'bg-orange-500'
                    }`}></span>
                    <p className="text-xs text-gray-500">{t(`furniture.condition.${item.condition}`)}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      )}

      {/* How it works - simplified for students */}
      <div className="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8 fade-in-section">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900">{t('home.howItWorks')}</h2>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
            {t('home.howItWorksSubtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold text-xl mb-6">1</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('home.howItWorksStep1')}</h3>
            <p className="text-gray-600">
              {t('home.howItWorksStep1Desc')}
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold text-xl mb-6">2</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('home.howItWorksStep2')}</h3>
            <p className="text-gray-600">
              {t('home.howItWorksStep2Desc')}
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold text-xl mb-6">3</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('home.howItWorksStep3')}</h3>
            <p className="text-gray-600">
              {t('home.howItWorksStep3Desc')}
            </p>
          </div>
        </div>
      </div>
      
      {/* 1stDibs inspired promise section */}
      <div className="bg-gray-50 py-12 sm:py-16 fade-in-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900">{t('home.promise')}</h2>
            <p className="mt-2 text-lg text-gray-600">{t('home.promiseSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="text-center">
              <div className="flex items-center justify-center mx-auto w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900">{t('home.eduVerified')}</h3>
              <p className="mt-2 text-xs text-gray-500">{t('home.eduVerifiedDesc')}</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mx-auto w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900">{t('home.structuredSearch')}</h3>
              <p className="mt-2 text-xs text-gray-500">{t('home.structuredSearchDesc')}</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mx-auto w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900">{t('home.directContact')}</h3>
              <p className="mt-2 text-xs text-gray-500">{t('home.directContactDesc')}</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mx-auto w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900">{t('home.nearbyPickup')}</h3>
              <p className="mt-2 text-xs text-gray-500">{t('home.nearbyPickupDesc')}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-indigo-700 fade-in-section">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            <span className="block">{t('home.ctaTitle1')}</span>
            <span className="block text-indigo-200">{t('home.ctaTitle2')}</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0 space-x-4">
            <Link to="/furniture" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50">
              {t('home.browseNow')}
            </Link>
            <Link to="/register" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
              {t('home.createAccount')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
